import { HideObjectType } from '../../../../@types/enums';
import { LAYER_OPTIONS } from '../../../../Constants';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { invalidateAiResultSessionCache } from '../../../../services/geminiService';
import { useViewerStore } from '../../../../store/store';
import { SetTopFitView } from '../Three-JS-Viewer/Controllers/CameraControl';
import { addModulesToAllRoofsMaxFill, removeGreyModulesAndSingleModuleRoofs, SetModuleGraph } from '../Three-JS-Viewer/Controllers/ModuleControl';
import { prepareAllRoofShadeForAiRun } from '../Three-JS-Viewer/Controllers/RefreshShade';
import { clearPendingUnghostAlert } from '../Three-JS-Viewer/Controllers/TreeImpactEligibility';
import { clearWishlistCacheForRun } from './utils/ai-layout';
import { clearAiLayoutSnapshotStateForRun } from './utils/ai-layout';

type RunAiPreludeArgs = {
    selectedRoofCombinationKey: string;
    sceneViewer: any;
    setAiScenariosSectionVisible: (next: boolean) => void;
    setIsDashboardOpen: (next: boolean) => void;
    setPathname: (next: string) => void;
    setAiError: (next: string | null) => void;
    setAiResult: (next: string | null) => void;
    setAiLoading: (next: boolean) => void;
    setAiRunStartMs: (next: number) => void;
    setAiRunElapsedMs: (next: number | null) => void;
    setAiLoadingStep: (next: string) => void;
    runGhostScenariosAndSave: () => Promise<void>;
    buildAiModuleFillingInput: () => any[];
    /** Close layout-options modals/sub-panels before `aiLoading` flips (avoids one-frame render loops on re-run). */
    onBeforeAiLoading?: () => void;
};

export async function runAiPreludeAction(args: RunAiPreludeArgs): Promise<{
    shouldContinue: boolean;
    apiKey: string;
    startMs: number;
    layoutAtRunStart: string;
    scenarios: any[];
}> {
    const {
        selectedRoofCombinationKey,
        sceneViewer,
        setAiScenariosSectionVisible,
        setIsDashboardOpen,
        setPathname,
        setAiError,
        setAiResult,
        setAiLoading,
        setAiRunStartMs,
        setAiRunElapsedMs,
        setAiLoadingStep,
        runGhostScenariosAndSave,
        buildAiModuleFillingInput,
        onBeforeAiLoading,
    } = args;

    if (useOneButtonStore.getState().dashboardContactsModalOpen) {
        return { shouldContinue: false, apiKey: '', startMs: Date.now(), layoutAtRunStart: selectedRoofCombinationKey || 'default', scenarios: [] };
    }

    setAiScenariosSectionVisible(true);
    const layoutAtRunStart = selectedRoofCombinationKey || 'default';
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
        setAiError('VITE_GEMINI_API_KEY is not set.');
        setAiResult(null);
        return { shouldContinue: false, apiKey: '', startMs: Date.now(), layoutAtRunStart, scenarios: [] };
    }

    onBeforeAiLoading?.();
    setAiLoading(true);
    useViewerStore.setState({ loadingSavedResult: false } as any);
    setIsDashboardOpen(false);
    clearPendingUnghostAlert();
    try {
        sceneViewer?.removeViewerAlert?.();
    } catch {
        // ignore alert dismissal errors
    }
    setPathname('/configure/tree');
    invalidateAiResultSessionCache();
    clearAiLayoutSnapshotStateForRun();
    clearWishlistCacheForRun();
    const startMs = Date.now();
    setAiRunStartMs(startMs);
    setAiRunElapsedMs(null);
    setAiError(null);

    try {
        if (sceneViewer) {
            useViewerStore.setState({ isHideAllGhostTrees: false } as any);
            sceneViewer.setVisibleGroup?.(LAYER_OPTIONS.MODEL.TREE, true);
            sceneViewer.hideObject?.(HideObjectType.GhostTree, false);
        }
    } catch {
        // ignore visibility errors
    }

    setAiResult(null);

    const self = sceneViewer;
    if (self) {
        try {
            self.setCameraView?.('3d');
            self.setVisibleGroup?.('maps', true);
            if ((self as any).mapCustom) (self as any).mapCustom.visible = true;
            self.setVisibleGroup?.('tree', true);
            self.hideObject?.(HideObjectType.GhostTree, false);
            SetTopFitView(self as any, {
                includeRoofs: true,
                includeTrees: true,
                includeObstructions: true,
                includeMapCustom: true,
                padding: 1.25,
                minZoom: 0.35,
                maxZoom: 12,
                height: 130,
            });
        } catch {
            // ignore camera/visibility issues
        }
        setAiLoadingStep('Calculating shade for all roofs...');
        await prepareAllRoofShadeForAiRun(self);
        setAiLoadingStep('Calculating tree-cutting scenarios...');
        addModulesToAllRoofsMaxFill(self);
        await SetModuleGraph(self);
        const allRoofIds = (self.roofMeshArr ?? [])
            .map((roof: { roofFaceId?: string }) => roof?.roofFaceId)
            .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);
        if (allRoofIds.length) {
            removeGreyModulesAndSingleModuleRoofs(self, allRoofIds);
            await SetModuleGraph(self);
        }
    }

    setAiLoadingStep('Calculating tree-cutting scenarios...');
    await runGhostScenariosAndSave();
    const err = (useViewerStore.getState() as any).aiModuleFillingError;
    if (err) {
        setAiError(err);
        return { shouldContinue: false, apiKey, startMs, layoutAtRunStart, scenarios: [] };
    }

    const scenarios = buildAiModuleFillingInput();
    if (!scenarios.length) {
        setAiResult('No scenario data (no roofs or trees). Run shade calculation first.');
        return { shouldContinue: false, apiKey, startMs, layoutAtRunStart, scenarios: [] };
    }

    return { shouldContinue: true, apiKey, startMs, layoutAtRunStart, scenarios };
}
